// Чистые функции для работы с объектами

export function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null) return false
  if (Array.isArray(value) || value instanceof Date) return false

  const proto = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

/**
 * Нормализует значение для устойчивого сравнения (используется при построении ключей кэша):
 * рекурсивно сортирует ключи объектов и выбрасывает поля со значением `undefined`.
 * Благодаря этому `{ a: 1, b: undefined }` и `{ a: 1 }` дают одинаковый ключ.
 */
export function normalizeInput<T>(input: T): T {
  if (Array.isArray(input)) {
    return input.map((item) => normalizeInput(item)) as unknown as T
  }

  if (!isPlainObject(input)) return input

  const sortedEntries = Object.keys(input)
    .filter((key) => input[key] !== undefined)
    .sort()
    .map((key) => [key, normalizeInput(input[key])] as const)

  return Object.fromEntries(sortedEntries) as T
}

export function omit<T extends Record<string, unknown>, K extends keyof T>(
  obj: T,
  keys: readonly K[],
): Omit<T, K> {
  const result = { ...obj }
  for (const key of keys) {
    delete result[key]
  }
  return result as Omit<T, K>
}

export function pick<T extends Record<string, unknown>, K extends keyof T>(
  obj: T,
  keys: readonly K[],
): Pick<T, K> {
  const result = {} as Pick<T, K>
  for (const key of keys) {
    if (key in obj) {
      result[key] = obj[key]
    }
  }
  return result
}

export function isEmptyObject(obj: Record<string, unknown>): boolean {
  return Object.keys(obj).length === 0
}
